import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Minus, Plus, Trash2, FileText, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { formatBaht } from '@/lib/money'
import { useFormTelemetry } from '@/hooks/useFormTelemetry'
import { Button, Card, DateInput, FormActions, Input, Select, SearchableDropdown } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { PreFilledBadge } from '@/components/ui/PreFilledBadge'
import {
  useCreateInvoice,
  useUpdateInvoice,
  useInvoicePriceCheck,
  type PriceCheckContext,
} from '@/hooks/useInvoices'
import { useLeases, useLease } from '@/hooks/useLeases'
import { useCustomer } from '@/hooks/useCustomers'
import { useAllProperties } from '@/hooks/useProperties'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday, formatMonthYear } from '@/lib/date'
import { generateBillingPeriods } from '@/lib/billingPeriod'
// WEB-H7: PDF generator is dynamic-imported at the call site (pdfme weight).
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Invoice, Lease } from '@/types'
import type { Template } from '@pdfme/common'

// ---------------------------------------------------------------------------
// Internal form item shape
// ---------------------------------------------------------------------------
interface InvoiceItem {
  item_type: string
  description: string
  quantity: number
  unit_price: number
  amount: number
  // WEB-M5: stable local identity for React keys — the add/remove list keyed
  // by index made input state stick to the wrong row on middle-item delete.
  // Never sent meaningfully to the API (strong params ignore it).
  _uid?: number
}

// Monotonic local uid for invoice-item rows (client-side identity only).
let invoiceItemUidCounter = 0
const nextItemUid = () => ++invoiceItemUidCounter

// Coerce any value to a finite number, else 0. Two sources need this:
//  - A number-input's raw string mid-edit: "", "-", "1e", "1.2.3" parse to NaN
//    (the Android WebView is especially prone to emitting these).
//  - API payloads: Rails serializes `decimal` columns (quantity/unit_price/
//    amount) as JSON *strings* ("5000.0"). Left as strings they (a) fail the
//    `Number.isFinite` input guard → blank fields, and (b) string-CONCATENATE in
//    the footer's `reduce(sum + amount)` → "฿NaN". Coercing at every boundary
//    keeps item state purely numeric.
const toFiniteNumber = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// DAT-1: read a Date's own local y/m/d fields into "YYYY-MM-DD", never a
// toISOString round trip (that reads the UTC calendar day, which shifts
// whenever the runtime's system zone isn't UTC).
const toDateOnlyString = (d: Date): string => {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// MON-8 (T149-018): mirrors the server's due_at_for_property
// (invoices_controller.rb, used by bulk generate) so a manually created
// invoice defaults its due date the same way a bulk-generated one would.
// A property with no payment_due_day keeps the pre-MON-8 rule: billedAt
// (the real issue date) + 15 days. A property WITH a payment_due_day gets a
// due date inside the BILLING PERIOD's month (decision C: not the issue
// date's month), clamped to that month's last day.
const computeDefaultDueAt = (
  billingPeriod: string,
  paymentDueDay: number | null | undefined,
  billedAt: string,
): string => {
  if (!paymentDueDay) {
    const [y, m, d] = billedAt.split('-').map(Number)
    const due = new Date(y, m - 1, d)
    due.setDate(due.getDate() + 15)
    return toDateOnlyString(due)
  }
  const [year, month] = billingPeriod.split('-').map(Number)
  // Day 0 of the NEXT month (JS Date's month param is 0-indexed, so passing
  // the already-1-indexed `month` here lands one month ahead) rolls back to
  // the last day of the TARGET month.
  const lastDayOfMonth = new Date(year, month, 0).getDate()
  const day = Math.min(paymentDueDay, lastDayOfMonth)
  return toDateOnlyString(new Date(year, month - 1, day))
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------
export interface InvoiceFieldsProps {
  /**
   * Null → create mode (POST /api/invoices).
   * Non-null → edit mode (PATCH /api/invoices/:id).
   */
  invoice: Invoice | null
  /**
   * Pre-selects the lease dropdown on create.
   * When set, customer and unit auto-fill from the selected lease, and
   * PreFilledBadge appears beside their labels.
   * Ignored in edit mode.
   */
  defaultLeaseId?: number
  /**
   * For external-parker create: when provided without a lease, the form
   * creates an invoice with customer_id (no lease_id). Sets a locked
   * customer display row and disables lease selection.
   */
  externalCustomerId?: number
  /**
   * Pre-selects the property filter on create. Lets a parent (e.g. the
   * onboarding wizard, which knows which property the user just set up)
   * narrow the lease dropdown immediately without locking the user in —
   * they can still switch back to "All properties". Ignored in edit mode
   * and when defaultLeaseId is provided.
   */
  defaultPropertyFilterId?: number
  /** Called with the saved Invoice after a successful POST or PATCH. */
  onSave: (saved: Invoice) => void
  /**
   * Optional HTML form id. When set, the <form> element receives this id so an
   * external <button form={formId} type="submit"> can trigger submission.
   */
  formId?: string
  /**
   * Reports whether the create-mode price-check is still resolving (true =
   * not yet priced, items are the unpriced placeholder). A parent driving an
   * EXTERNAL submit button (e.g. the onboarding wizard's Save & Continue) uses
   * this to disable that button until the engine numbers land — without it, a
   * click that beats the async price-check submits a ฿0 invoice and is silently
   * rejected by the zero-total guard. No-op for self-submitting standalone use.
   */
  onPricingStateChange?: (loading: boolean) => void
  /**
   * EP-86 T013 — Streamlined wizard mode (S2-1).
   *
   * false (default): render all sections visible — Invoice Details card
   * (Essentials) + Invoice Items card (Advanced) in the standard 3-column grid.
   *
   * true (wizard only — passed by OnboardingWizardPage): Invoice Details card
   * stays inline; the Invoice Items editor is collapsed into an
   * "Advanced options" disclosure (OptionalSection) closed by default. Line
   * items are still auto-generated from the pricing engine when streamlined;
   * the disclosure lets the operator review/edit them if needed. Do NOT
   * piggyback this on `formId` (S2-1 decision).
   */
  streamlinedMode?: boolean
  className?: string
  /** Fires with the mutation-pending state so the wizard footer Save can spin. */
  onSubmittingChange?: (submitting: boolean) => void
  /**
   * EP-130 (T130-014, GAP-1) — the deposit-settlement "Bill shortfall"
   * deep-link. When set (create mode only), appended as one extra invoice
   * line once the price-check for defaultLeaseId resolves, alongside
   * whatever rows the engine (or the fallback rent row) produces — never
   * replaces them. Applied at most once per mount.
   */
  prefillItem?: { item_type: string; description: string; amount: number }
  /**
   * T149-022 (LSE-7 decision A): on an invoice with any payment, only the
   * owner reaches this form, and even then only billed_at/due_at/
   * billing_period may change. Edit mode only (a create-mode invoice never
   * has payments yet). Items, add/remove and the total render read-only;
   * submit sends ONLY those three date/period fields, matching the
   * server's raw-key check (invoices_controller.rb
   * paid_invoice_disallowed_keys).
   */
  restrictedMode?: boolean
}

/**
 * Self-contained invoice form.
 *
 * Owns its own state and mutation hooks so it can be embedded in any context:
 * InvoiceFormPage, the onboarding wizard Step 7, or a quick-add surface.
 *
 * Create path (tenant):          POST /api/invoices (lease_id)   → onSave
 * Create path (external parker): POST /api/invoices (customer_id) → onSave
 * Edit path:                     PATCH /api/invoices/:id          → onSave
 *
 * Preserved behaviours:
 * - Property filter dropdown (new, non-preselected, non-external flows),
 *   listing every building on the account (MON-18 A: no narrowing to
 *   buildings found among active leases)
 * - Customer and unit auto-fill from selected lease
 * - Item rows pre-populate from PricingCalculator (useInvoicePriceCheck)
 * - Due date defaults to billed_at + 15 days (new invoices only)
 * - translateApiError error toasts
 * - data-tour-target="invoice-preview" on the preview panel
 * - Mobile: item rows render as card stack on <sm
 */
export function InvoiceFields({
  invoice,
  defaultLeaseId,
  externalCustomerId,
  defaultPropertyFilterId,
  onSave,
  formId,
  onPricingStateChange,
  streamlinedMode = false,
  className,
  onSubmittingChange,
  prefillItem,
  restrictedMode = false,
}: InvoiceFieldsProps) {
  const { t, i18n } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')

  const isEditing = !!invoice
  const isExternalParker =
    !isEditing &&
    !!externalCustomerId &&
    !defaultLeaseId

  const translateError = useApiErrorTranslator('invoices')
  const addToast = useNotificationStore((s) => s.addToast)
  // EP-73 Tier-1: form_started:invoice / form_submitted:invoice lifecycle.
  const { markStarted, markSubmitted } = useFormTelemetry('invoice')
  const createInvoice = useCreateInvoice()
  const updateInvoice = useUpdateInvoice()

  // Report mutation-pending so the wizard footer Save can show a spinner.
  useEffect(() => {
    onSubmittingChange?.(createInvoice.isPending || updateInvoice.isPending)
  }, [createInvoice.isPending, updateInvoice.isPending, onSubmittingChange])
  const priceCheck = useInvoicePriceCheck()
  // Templates + account settings — needed for the wizard "Preview PDF" action
  // that replaces the inline submit when this form is embedded in onboarding.
  const { data: invoiceTemplates } = useDocumentTemplates(1, 'invoice')
  const { data: accountSettings } = useAccountSettings()
  const { format, timezone, calendar } = useLocalizationConfig()
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false)

  // -------------------------------------------------------------------------
  // Property filter
  // Shown only for new tenant-lease invoices where no defaultLeaseId given.
  // -------------------------------------------------------------------------
  // Show the property filter in create mode for tenant-lease invoices.
  // Historically this was hidden when `defaultLeaseId` was set (the user
  // had drilled into a specific lease and the filter felt redundant). The
  // onboarding wizard pre-selects BOTH the property AND a default lease,
  // and the user needs the property filter to switch among the 100+
  // imported leases — so we keep it visible whenever the parent has
  // explicitly passed `defaultPropertyFilterId`.
  const showPropertyFilter =
    !isEditing && !isExternalParker && (!defaultLeaseId || !!defaultPropertyFilterId)
  // Pre-seed from defaultPropertyFilterId when the parent provides one
  // (e.g. onboarding wizard knows the property the user just created).
  // The user can still change this to null ("All properties") afterwards.
  const [propertyFilterId, setPropertyFilterId] = useState<number | null>(
    defaultPropertyFilterId ?? null,
  )

  // MON-18 A: every building on the account is listed, with no narrowing to
  // buildings found among a page-1 probe of active leases. That narrowing
  // (B-018's propertiesForInvoiceFilter) hid a building whose active leases
  // all fell past page 1 of a large account, and hid a building with zero
  // active leases entirely.
  const { data: properties = [] } = useAllProperties(showPropertyFilter)

  // -------------------------------------------------------------------------
  // Lease dropdown (EP-116 T008 — paged + load-more, mirrors ParcelFormPage's
  // search/page/accumulate shape).
  // -------------------------------------------------------------------------
  const [leaseSearch, setLeaseSearch] = useState('')
  const [leasePage, setLeasePage] = useState(1)
  const [accumulatedLeases, setAccumulatedLeases] = useState<Lease[]>([])
  const { data: leasesData, isLoading: isLoadingLeases } = useLeases(
    leasePage,
    'active',
    !isExternalParker,
    leaseSearch || undefined,
    propertyFilterId ?? undefined,
  )

  // Reset the accumulated list when the search query or property filter
  // changes -- a new query/filter must not append onto stale results.
  useEffect(() => {
    setLeasePage(1)
    setAccumulatedLeases([])
  }, [leaseSearch, propertyFilterId])

  // Append new page results onto the accumulated list; replace on page 1.
  useEffect(() => {
    if (!leasesData?.data) return
    setAccumulatedLeases((prev) => {
      if (leasePage === 1) return leasesData.data
      const existingIds = new Set(prev.map((l) => l.id))
      return [...prev, ...leasesData.data.filter((l) => !existingIds.has(l.id))]
    })
  }, [leasesData, leasePage])
  const hasMoreLeases = !!leasesData?.meta && leasesData.meta.current_page < leasesData.meta.total_pages

  const [leaseId, setLeaseId] = useState<string>(
    isEditing
      ? String(invoice.lease_id ?? '')
      : defaultLeaseId
        ? String(defaultLeaseId)
        : '',
  )

  // -------------------------------------------------------------------------
  // EP-55 F-003: fallback lease fetch for wizard-created drafts not in the
  // active-leases query result. See `leaseOptions` block for details.
  // EP-116 T008: `activeLeaseIds` now covers every ACCUMULATED page, not just
  // the current one -- otherwise loading page 2+ (which may include the
  // fallback-fetched lease, e.g. once the user searches or pages far enough
  // to reach it) would never turn `needFallbackLease` off, leaving a
  // duplicate prepended entry alongside the real one in `leaseOptions`.
  // -------------------------------------------------------------------------
  const activeLeaseIds = new Set(accumulatedLeases.map((l) => l.id))
  const needFallbackLease = !!defaultLeaseId && !activeLeaseIds.has(Number(defaultLeaseId))
  const { data: fallbackLease } = useLease(defaultLeaseId ?? 0, needFallbackLease)

  // -------------------------------------------------------------------------
  // Customer + unit display derived from the selected lease
  // -------------------------------------------------------------------------
  const selectedLease =
    accumulatedLeases.find((l) => l.id === Number(leaseId)) ??
    (fallbackLease && fallbackLease.id === Number(leaseId) ? fallbackLease : undefined)
  const preFilledCustomerName = selectedLease?.customer?.name ?? null
  const preFilledUnitName = selectedLease?.unit?.name ?? null

  // Track per-field manual-override state so we can clear the corresponding badge.
  // Starts as pre-filled whenever a lease is selected (via defaultLeaseId or manual pick)
  // in create mode. Cleared independently when the user explicitly clears the lease or
  // when we detect the underlying field has been overridden (future-proof hook-point).
  const [customerBadge, setCustomerBadge] = useState(() => !isEditing && !!defaultLeaseId)
  const [unitBadge, setUnitBadge] = useState(() => !isEditing && !!defaultLeaseId)

  // Sync badges when leaseId changes in create mode:
  //  - lease selected → show both badges
  //  - lease cleared  → hide both badges
  const prevLeaseIdRef = useRef(leaseId)
  useEffect(() => {
    if (isEditing) return
    const prev = prevLeaseIdRef.current
    prevLeaseIdRef.current = leaseId

    if (leaseId && leaseId !== prev) {
      // Lease newly selected (or pre-filled from defaultLeaseId on mount)
      setCustomerBadge(true)
      setUnitBadge(true)
    } else if (!leaseId && prev) {
      // Lease cleared
      setCustomerBadge(false)
      setUnitBadge(false)
    }
  }, [leaseId, isEditing])

  // Badge visibility: only in create mode and when the corresponding flag is
  // set. Also gated on wizard mode (`formId != null`) — the "filled from
  // previous step" label only makes sense inside the multi-step wizard.
  const showPreFilledBadges = !!formId
  const showCustomerBadge = showPreFilledBadges && !isEditing && customerBadge && !!selectedLease
  const showUnitBadge = showPreFilledBadges && !isEditing && unitBadge && !!selectedLease

  // External parker — fetch customer for display
  const { data: externalCustomer } = useCustomer(externalCustomerId ?? 0)

  // -------------------------------------------------------------------------
  // Dates
  // -------------------------------------------------------------------------
  const [billedAt, setBilledAt] = useState(
    isEditing ? invoice.billed_at.split('T')[0] : localToday(timezone),
  )
  const [dueAt, setDueAt] = useState(isEditing ? invoice.due_at.split('T')[0] : '')

  // MON-9/MON-10: the billing period the invoice covers, stored separately
  // from billedAt (the real issue date, never backdated). Defaults to
  // billedAt's month once at mount and does not track further billedAt
  // edits. An operator issuing an invoice today for an earlier period
  // (a backfill) must be able to move billedAt without also moving the
  // period back to "now".
  const [invoicePeriod, setInvoicePeriod] = useState(
    isEditing ? (invoice.billing_period ?? invoice.billed_at.slice(0, 7)) : billedAt.slice(0, 7),
  )

  // Last 12 months, in the active locale/calendar. In edit mode the invoice's
  // own period is prepended when it falls outside that window (an old
  // invoice), mirroring leaseOptions' fallback-lease pattern so the Select
  // never renders blank.
  const billingPeriodOptions = useMemo(() => {
    const base = generateBillingPeriods({ locale: i18n.language, calendar })
    if (base.some((o) => o.value === invoicePeriod)) return base
    const [year, month] = invoicePeriod.split('-').map(Number)
    const label = formatMonthYear(new Date(year, month - 1, 1), { locale: i18n.language, calendar })
    return [{ value: invoicePeriod, label }, ...base]
  }, [i18n.language, calendar, invoicePeriod])

  // MON-8 (T149-018): true once the operator has hand-typed a due date, so
  // the auto-default effect below stops overwriting it (mirrors the file's
  // existing userEditedDescriptionTypesRef pattern for item descriptions).
  const dueAtEditedRef = useRef(false)

  // Default due date: the selected lease's property payment_due_day inside
  // the billing period's month (clamped to month end), or billedAt + 15 days
  // when the property has none set. Same rule invoices_controller.rb's
  // due_at_for_property applies for bulk generation (computeDefaultDueAt
  // above). Re-runs when the lease or the billing period changes (a
  // different lease means a different property; a different period moves
  // where the payment-due-day anchor lands), but never once the operator has
  // typed their own due date.
  useEffect(() => {
    if (!billedAt || isEditing) return
    if (dueAtEditedRef.current) return
    const paymentDueDay = selectedLease?.unit?.room_type?.property?.payment_due_day
    setDueAt(computeDefaultDueAt(invoicePeriod, paymentDueDay, billedAt))
  }, [billedAt, isEditing, invoicePeriod, selectedLease])

  // -------------------------------------------------------------------------
  // Item rows
  // -------------------------------------------------------------------------
  // "No calculation on frontend" — the Ruby pricing engine owns ALL invoice
  // math. We never compute qty×price or percentage rows in JS. Instead the
  // lease price-check returns the resolved engine inputs (`pricing_context`),
  // and an inline quantity edit re-prices through POST /api/pricing_calculation
  // (the same engine), rendering the returned JSON. These refs/state carry the
  // context + the metadata needed to merge the engine result back into `items`.
  const [pricingContext, setPricingContext] = useState<PriceCheckContext | null>(null)
  // True once the create-mode price-check has resolved (success OR fallback) and
  // `items` reflect real engine numbers rather than the unpriced placeholder.
  // Surfaced via onPricingStateChange so a wizard's external Save & Continue can
  // stay disabled until pricing lands (see the prop docs + the report effect).
  const [hasPriced, setHasPriced] = useState(false)
  // item_types the engine actually emits (rent + formula keys + their vat). A
  // row of one of these types is engine-owned: its amount/description come from
  // the server and its rate is read-only. Everything else (common_area_fee*,
  // manual "other"/discount rows) is passthrough, preserved across re-prices.
  const engineManagedTypesRef = useRef<Set<string>>(new Set())
  // meter serial tokens ("SN123") keyed by item_type, captured from the
  // price-check's serial-decorated descriptions so we can re-apply them to the
  // stateless engine's plain descriptions (display string only — not math).
  const serialMapRef = useRef<Record<string, string>>({})
  // item_types whose description the operator hand-typed (LSE-7 decision D,
  // 05-F13). A quantity edit re-prices through the engine and used to
  // overwrite EVERY row's description with the engine's, discarding a
  // hand-typed one the moment the quantity changed. Keyed by item_type
  // (mirrors engineManagedTypesRef/serialMapRef) since applyEngineItems
  // matches rows by item_type, not row identity.
  const userEditedDescriptionTypesRef = useRef<Set<string>>(new Set())
  // Gate the re-price effect to fire only AFTER a user quantity edit, so the
  // initial price-check load doesn't trigger a redundant engine round-trip.
  const hasUserEditedRef = useRef(false)
  const repriceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isRepricing, setIsRepricing] = useState(false)
  // EP-130 (T130-014, GAP-1) — apply the ?item_type=&description=&amount=
  // deep-link prefill at most once, regardless of how many times the
  // price-check effect below re-runs (e.g. billedAt changes).
  const prefillAppliedRef = useRef(false)

  const isMeteredType = (type: string) =>
    ['water', 'electricity', 'internet'].includes(type)

  // A row is engine-owned when we have a pricing context and the engine emits
  // its type. Such rows render read-only (rate is formula-derived); metered
  // rows keep an editable QUANTITY (meter-misread correction) that re-prices.
  // `discount` is excluded: the operator hand-enters discount amounts, so it
  // stays a manual row even when a formula discount also exists.
  const isEngineOwned = (type: string) =>
    !!pricingContext && type !== 'discount' && engineManagedTypesRef.current.has(type)

  // common_fee percentage rate (for the "X% of subtotal" disclosure) read
  // straight from the server-resolved formula — not computed here.
  const commonFeeRate =
    pricingContext?.pricing_formula.find(
      (fi) => fi.key === 'common_fee' && fi.type === 'percentage',
    )?.rate ?? null

  // Capture serial tokens from "BaseLabel (SN123): ..." descriptions.
  const captureSerials = (rows: { item_type: string; description: string }[]) => {
    const map: Record<string, string> = {}
    rows.forEach((r) => {
      const m = r.description.match(/^[^:]+ \(([^)]+)\):/)
      if (m) map[r.item_type] = m[1]
    })
    serialMapRef.current = map
  }
  // Re-insert a captured serial into a plain engine description.
  const applySerial = (type: string, desc: string): string => {
    const serial = serialMapRef.current[type]
    if (!serial || desc.includes(`(${serial})`)) return desc
    return desc.replace(/^([^:]+):/, `$1 (${serial}):`)
  }
  // Record which types the engine owns (excluding DB-backed common_area_fee*).
  const rememberEngineTypes = (rows: { item_type: string }[]) => {
    engineManagedTypesRef.current = new Set(
      rows.map((r) => r.item_type).filter((t) => !t.startsWith('common_area_fee')),
    )
  }
  // Replace engine-owned rows with the engine's result; keep passthrough rows
  // (common_area_fee*, manual rows) verbatim. Engine rows lead, matching the
  // original price-check ordering (formula order, then appended fees).
  // LSE-7 decision D: a row whose description the operator hand-typed keeps
  // it across the re-price (looked up from the row it's replacing, by
  // item_type) instead of taking the engine's description; every other row
  // still takes the engine's description (with the serial re-inserted).
  const applyEngineItems = (engineItems: InvoiceItem[]) => {
    const engineTypes = new Set(engineItems.map((i) => i.item_type))
    rememberEngineTypes(engineItems)
    setItems((prev) => {
      const passthrough = prev.filter((it) => !engineTypes.has(it.item_type))
      const decorated = engineItems.map((ei) => {
        const userEditedDescription = userEditedDescriptionTypesRef.current.has(ei.item_type)
        const previousRow = userEditedDescription
          ? prev.find((it) => it.item_type === ei.item_type)
          : undefined
        return {
          _uid: nextItemUid(),
          item_type: ei.item_type,
          description: previousRow ? previousRow.description : applySerial(ei.item_type, ei.description),
          quantity: toFiniteNumber(ei.quantity),
          unit_price: toFiniteNumber(ei.unit_price),
          amount: toFiniteNumber(ei.amount),
        }
      })
      return [...decorated, ...passthrough]
    })
  }

  const [items, setItems] = useState<InvoiceItem[]>(() => {
    if (isEditing && invoice.items?.length > 0) {
      return invoice.items.map((item) => {
        // Discounts are stored NEGATIVE in the DB, but the form convention is
        // "operator sees/enters a POSITIVE magnitude; negated on submit"
        // (normalizeItemsForSubmit). Surface the magnitude on load so the input
        // and the per-row "− ฿x" match create mode (no double-negative) and the
        // normalized footer total stays correct.
        // Coerce: API decimals arrive as strings ("5000.0"); left as strings the
        // footer reduce concatenates → "฿NaN" and the input guard blanks fields.
        const isDiscount = item.item_type === 'discount'
        const unitPrice = toFiniteNumber(item.unit_price)
        const amount = toFiniteNumber(item.amount)
        return {
          _uid: nextItemUid(),
          item_type: item.item_type,
          description: item.description,
          quantity: toFiniteNumber(item.quantity),
          unit_price: isDiscount ? Math.abs(unitPrice) : unitPrice,
          amount: isDiscount ? Math.abs(amount) : amount,
        }
      })
    }
    if (isExternalParker) {
      return [
        {
          item_type: 'parking',
          description: t('item_types.parking'),
          quantity: 1,
          unit_price: 0,
          amount: 0,
        },
      ]
    }
    return [
      {
        item_type: 'rent',
        description: t('form.default_rent_description'),
        quantity: 1,
        unit_price: 0,
        amount: 0,
      },
    ]
  })

  // Pre-populate items from PricingCalculator when lease changes (new invoices only)
  useEffect(() => {
    if (!leaseId || isEditing) return

    const billingPeriod = billedAt
      ? billedAt.slice(0, 7)
      : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`

    priceCheck.mutate(
      { leaseId: Number(leaseId), billingPeriod },
      {
        onSuccess: (data) => {
          // Pricing has landed (whether the engine returned rows or we fall
          // back below) — release any external submit button gated on this.
          setHasPriced(true)
          // Preserve every row priceCheck returns, including zero-quantity
          // rows. Thai tax-invoice compliance requires every contracted line
          // item (rent + each metered service + fees) to appear on the saved
          // invoice and the PDF, even when usage is zero or the meter has no
          // reading. Form inputs use min={0} so the user can save without
          // friction.
          if (data.items.length > 0) {
            // Stash the resolved engine inputs + which types the engine owns +
            // the serial tokens, so a later quantity edit re-prices through the
            // engine (no JS pricing math). hasUserEdited stays false so this
            // initial load doesn't trigger a redundant re-price round-trip.
            if (data.pricing_context) setPricingContext(data.pricing_context)
            captureSerials(data.items)
            rememberEngineTypes(data.items)
            hasUserEditedRef.current = false
            setItems(
              data.items.map((item) => ({
                _uid: nextItemUid(),
                item_type: item.item_type,
                description: item.description,
                quantity: toFiniteNumber(item.quantity),
                unit_price: toFiniteNumber(item.unit_price),
                amount: toFiniteNumber(item.amount),
              })),
            )
          } else {
            // Fallback: rent from unit price. Reuses `selectedLease` (accumulated
            // pages + the fallback-fetched lease) rather than re-deriving from the
            // raw current-page `leasesData` -- EP-116 T008: with pagination, the
            // selected lease may no longer be on the freshest single page.
            const lease = selectedLease
            if (lease) {
              setItems([
                {
                  _uid: nextItemUid(),
                  item_type: 'rent',
                  description: t('form.default_rent_description'),
                  quantity: 1,
                  unit_price: toFiniteNumber(lease.unit?.price),
                  amount: toFiniteNumber(lease.unit?.price),
                },
              ])
            }
          }
          // EP-130 (T130-014, GAP-1) — append the deposit-shortfall deep-link
          // row once pricing has landed, on top of whichever rows the branches
          // above produced. Guarded so re-runs of this effect (e.g. billedAt
          // changing) never append a second copy.
          if (prefillItem && !prefillAppliedRef.current) {
            prefillAppliedRef.current = true
            setItems((prev) => [
              ...prev,
              {
                _uid: nextItemUid(),
                item_type: prefillItem.item_type,
                description: prefillItem.description,
                quantity: 1,
                unit_price: prefillItem.amount,
                amount: prefillItem.amount,
              },
            ])
          }
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId, billedAt, isEditing])

  // A fresh lease selection means the next render's items are unpriced again
  // until the price-check above resolves. Reset so a parent's gated submit
  // button re-disables while we re-price (covers the wizard's pre-selected
  // lease + any later manual lease switch).
  useEffect(() => {
    if (isEditing) return
    setHasPriced(false)
  }, [leaseId, isEditing])

  // Tell a parent driving an EXTERNAL submit button whether create-mode pricing
  // is still pending, so it can keep that button disabled until `items` reflect
  // real engine numbers — without this, a click that beats the async price-check
  // submits a ฿0 invoice that the zero-total guard silently rejects.
  useEffect(() => {
    if (!onPricingStateChange) return
    const pricingPending = !isEditing && !isExternalParker && !!leaseId && !hasPriced
    onPricingStateChange(pricingPending)
  }, [onPricingStateChange, isEditing, isExternalParker, leaseId, hasPriced])

  // EDIT mode: the saved invoice.items are the source of truth (the user may
  // have hand-adjusted them), so we must NOT overwrite them. But we still need
  // the pricing context so an inline quantity edit can re-price through the
  // engine. Fetch it ONCE for the invoice's lease and capture context/serials/
  // engine types only — items are left untouched.
  useEffect(() => {
    if (!isEditing || !invoice.lease_id) return
    const billingPeriod = invoice.billed_at
      ? invoice.billed_at.slice(0, 7)
      : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
    priceCheck.mutate(
      { leaseId: invoice.lease_id, billingPeriod },
      {
        onSuccess: (data) => {
          if (data.pricing_context) setPricingContext(data.pricing_context)
          captureSerials(data.items)
          rememberEngineTypes(data.items)
          hasUserEditedRef.current = false
          // intentionally NOT calling setItems — preserve invoice.items
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  // Re-price through the engine after a user quantity edit. Builds the
  // consumption map from the current metered rows and POSTs it to
  // /api/pricing_calculation (the same engine as the invoice generator), then
  // merges the returned rows back via applyEngineItems. Debounced 400ms so
  // rapid typing doesn't spam the endpoint. Fires only when hasUserEdited is
  // set (skips the initial load) and a pricing context exists.
  const meteredSignature = JSON.stringify(
    items
      .filter((it) => isMeteredType(it.item_type))
      .map((it) => [it.item_type, it.quantity]),
  )
  useEffect(() => {
    if (!pricingContext || !hasUserEditedRef.current) return
    if (repriceTimerRef.current) clearTimeout(repriceTimerRef.current)
    setIsRepricing(true)
    const consumption: Record<string, number> = {}
    items
      .filter((it) => isMeteredType(it.item_type))
      .forEach((it) => {
        consumption[it.item_type] = Number(it.quantity) || 0
      })
    repriceTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post<{
          items: InvoiceItem[]
          subtotal_before_percentage: number
          total: number
        }>('/api/pricing_calculation', {
          pricing_formula: pricingContext.pricing_formula,
          unit_price: pricingContext.unit_price,
          consumption,
          ...(pricingContext.unit_area_sqm != null && {
            unit_area_sqm: pricingContext.unit_area_sqm,
          }),
          ...(pricingContext.unit_share_ratio != null && {
            unit_share_ratio: pricingContext.unit_share_ratio,
          }),
        })
        applyEngineItems(data.items)
      } catch (err) {
        addToast({ type: 'error', message: translateError(err) })
      } finally {
        setIsRepricing(false)
      }
    }, 400)
    return () => {
      if (repriceTimerRef.current) clearTimeout(repriceTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meteredSignature, pricingContext])

  // -------------------------------------------------------------------------
  // Item manipulation helpers
  // -------------------------------------------------------------------------
  const itemTypeOptions = [
    { value: 'rent', label: t('item_types.rent') },
    { value: 'water', label: t('item_types.water') },
    { value: 'electricity', label: t('item_types.electricity') },
    { value: 'internet', label: t('item_types.internet') },
    { value: 'common_fee', label: t('item_types.common_fee') },
    { value: 'parking', label: t('item_types.parking') },
    { value: 'discount', label: t('item_types.discount') },
    { value: 'other', label: t('item_types.other') },
  ]

  // The list above is what an operator may hand-create, and it deliberately
  // omits every system-written type: `deposit` is billed by the lease
  // Bill-deposit action (offering it here would route around that endpoint's
  // one-deposit-per-lease guard), while common_area_fee / late_fee / promotion
  // and the `<key>_vat` siblings are appended by the pricing and promotion
  // engines. A row that ALREADY carries one of those must still show its own
  // label — <Select> renders an empty control for a value with no matching
  // option — so the current value joins the list for that row alone.
  const optionsForItem = (type: string) => {
    if (itemTypeOptions.some((o) => o.value === type)) return itemTypeOptions
    const key = `item_types.${type}`
    const label = i18n.exists(`invoices:${key}`) ? t(key) : type
    return [...itemTypeOptions, { value: type, label }]
  }

  // Default description for each item_type. Used when operator switches types
  // to reset the description field to a sensible default.
  const defaultDescriptionForType = (type: string): string => {
    if (type === 'discount') return tc('pricing_formula.default_discount_label')
    return ''
  }

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setItems((prev) => {
      const updated = [...prev]
      // When switching item_type, apply per-type defaults:
      //   - discount → default description + lock quantity to 1
      //   - any other type → reset description and quantity to defaults
      if (field === 'item_type') {
        const newType = value as string
        const prevType = updated[index].item_type
        if (newType !== prevType) {
          updated[index] = {
            ...updated[index],
            item_type: newType,
            description: defaultDescriptionForType(newType),
            quantity: 1,
            unit_price: 0,
            amount: 0,
          }
          return updated
        }
      }
      updated[index] = { ...updated[index], [field]: value }
      if (field === 'description') {
        // LSE-7 decision D: remember this row's type as hand-typed so a later
        // quantity edit's re-price (applyEngineItems) keeps the description
        // instead of overwriting it with the engine's.
        userEditedDescriptionTypesRef.current.add(updated[index].item_type)
      }
      if (field === 'quantity' || field === 'unit_price') {
        const editedType = updated[index].item_type
        if (isEngineOwned(editedType) && field === 'quantity') {
          // Engine-owned metered row: the QUANTITY is the only user input
          // (a meter-misread correction). Do NOT compute amount/description in
          // JS — flag the edit so the re-price effect calls the pricing engine,
          // which returns the authoritative amount AND the matching description
          // (fixes B-036's desync at the source). qty=0 metered rows still bill
          // ฿0 server-side and stay visible (Thai tax-invoice rule).
          hasUserEditedRef.current = true
        } else if (!isEngineOwned(editedType)) {
          // Manual row (no pricing context, or a hand-added "other"/discount
          // line the engine doesn't own): direct data entry, not formula math.
          // Discount stays positive here; negated in normalizeItemsForSubmit.
          // Guard both operands against NaN so a stray non-finite value (e.g.
          // from loaded string data) can never produce a NaN amount.
          const q = Number(updated[index].quantity)
          const p = Number(updated[index].unit_price)
          updated[index].amount = (Number.isFinite(q) ? q : 0) * (Number.isFinite(p) ? p : 0)
        }
        // Engine-owned non-metered rows (rent/common_fee/vat) render read-only,
        // so this branch is never reached for them.
      }
      return updated
    })
  }

  // Normalize items before sending to API: discount items must have negative
  // amount/unit_price regardless of what the operator typed (positive is expected
  // in the UI; negation happens here so the DB always stores discount items as
  // negative-amount InvoiceItems — matching the PricingEngine contract).
  const normalizeItemsForSubmit = (rawItems: InvoiceItem[]): InvoiceItem[] =>
    rawItems.map((item) => {
      if (item.item_type === 'discount') {
        const positiveAmount = Math.abs(item.amount)
        const positiveUnitPrice = Math.abs(item.unit_price)
        return {
          ...item,
          amount: -positiveAmount,
          unit_price: -positiveUnitPrice,
        }
      }
      return item
    })

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { _uid: nextItemUid(), item_type: 'other', description: '', quantity: 1, unit_price: 0, amount: 0 },
    ])
  }

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems((prev) => prev.filter((_, i) => i !== index))
    }
  }

  // The displayed footer total AND the submit/preview guards both use the
  // NORMALIZED total (discount rows forced negative — exactly what gets saved),
  // so the on-screen "Total" can never disagree with the persisted invoice
  // total. Previously the footer summed the raw positive amounts and
  // over-counted discounts (showed rent + |discount| instead of rent − discount).
  const normalizedTotal = normalizeItemsForSubmit(items).reduce((sum, item) => sum + item.amount, 0)

  const handleLeaseSearch = useCallback((query: string) => {
    setLeaseSearch(query)
  }, [])

  // EP-55 F-003: when `defaultLeaseId` is provided AND missing from the
  // active-leases query result (typical wizard scenario where the just-created
  // lease is `status='draft'`, not 'active'), prepend the synthesized lease
  // option so the combobox shows it correctly. The fetch happens at the top of
  // this component (see `fallbackLease`).
  const leaseOptions = (() => {
    const base = accumulatedLeases.map((l) => ({
      value: String(l.id),
      label: `${l.lease_code} - ${l.customer?.name || 'Unknown'} (${t('labels.unit')} ${l.unit?.name || 'Unknown'})`,
    }))
    if (needFallbackLease && fallbackLease) {
      return [
        {
          value: String(fallbackLease.id),
          label: `${fallbackLease.lease_code} - ${fallbackLease.customer?.name || 'Unknown'} (${t('labels.unit')} ${fallbackLease.unit?.name || 'Unknown'})`,
        },
        ...base,
      ]
    }
    return base
  })()

  // -------------------------------------------------------------------------
  // Track the just-created invoice during Preview so subsequent clicks
  // UPDATE the same record instead of POSTing a new one. Cleared only when
  // the form unmounts; the wizard's onSave (advance) reads the same value
  // so it doesn't double-create either.
  // -------------------------------------------------------------------------
  const [previewSavedInvoice, setPreviewSavedInvoice] = useState<Invoice | null>(null)

  // Shared persistence helper used by both Save & Continue and Preview.
  // Returns the live saved Invoice (creates first time, patches on re-use).
  //
  // Zero-amount items are PRESERVED — Thai tax-invoice compliance requires
  // every contracted line item to be visible on the invoice/PDF, even at ฿0.
  // The backend (PricingCalculator/PricingEngine) now emits zero rows for
  // every metered formula item so auditors can see the line was billed at zero
  // this period rather than silently absent.
  //
  // Discount items are negated here (normalizeItemsForSubmit) so the DB always
  // stores them as negative-amount InvoiceItems regardless of what the operator
  // typed in the UI (positive input is expected — negation is the form's job).
  const persistInvoice = async (): Promise<Invoice> => {
    // T149-022 (LSE-7): a paid invoice's restricted save sends ONLY the
    // three date/period keys, never `items` or `total`, even though the
    // (unchanged) items/total are sitting right there in local state. The
    // server's paid_invoice_disallowed_keys check 422s on any other key
    // present under `invoice`, so this must be a genuinely smaller payload,
    // not the normal one with items/total re-sent unchanged.
    if (restrictedMode && isEditing) {
      return await updateInvoice.mutateAsync({
        id: invoice.id,
        billed_at: billedAt,
        due_at: dueAt,
        billing_period: invoicePeriod,
      })
    }

    const normalizedItems = normalizeItemsForSubmit(items)
    const normalizedTotal = normalizedItems.reduce((sum, item) => sum + item.amount, 0)
    const payload = {
      billed_at: billedAt,
      due_at: dueAt,
      billing_period: invoicePeriod,
      total: normalizedTotal,
      items: normalizedItems,
    }
    if (isEditing) {
      return await updateInvoice.mutateAsync({ id: invoice.id, ...payload })
    }
    if (previewSavedInvoice) {
      // Re-click: keep the same DB row, just refresh its values
      const updated = await updateInvoice.mutateAsync({
        id: previewSavedInvoice.id,
        ...payload,
      })
      setPreviewSavedInvoice(updated)
      return updated
    }
    let saved: Invoice
    if (isExternalParker) {
      saved = await createInvoice.mutateAsync({
        customer_id: externalCustomerId,
        ...payload,
      })
    } else {
      saved = await createInvoice.mutateAsync({
        lease_id: Number(leaseId),
        ...payload,
      })
    }
    setPreviewSavedInvoice(saved)
    return saved
  }

  // -------------------------------------------------------------------------
  // Preview PDF — saves the invoice (or updates the previously-saved one),
  // then opens the rendered PDF in a new tab. The wizard's Step 7 surfaces
  // this so the user previews the *real* persisted record, not a draft.
  // -------------------------------------------------------------------------
  const handlePreviewPdf = async () => {
    const tmpl = invoiceTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) {
      addToast({ type: 'error', message: t('messages.no_invoice_template') })
      return
    }
    if (!isExternalParker && !leaseId) return
    // Use normalizedTotal: discount items are negated, so a net-zero/negative
    // invoice is allowed (backend skips only zero-total). Surface an explicit
    // error rather than silently doing nothing.
    if (!billedAt || !dueAt) {
      addToast({ type: 'error', message: t('errors.fields.billed_at') })
      return
    }
    if (normalizedTotal === 0) {
      addToast({ type: 'error', message: t('form.error_zero_total') })
      return
    }

    setIsPreviewingPdf(true)
    try {
      const saved = await persistInvoice()
      const { previewInvoicePdf } = await import('@/lib/invoicePdfGenerator')
      await previewInvoicePdf(saved, tmpl, accountSettings)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setIsPreviewingPdf(false)
    }
  }

  // -------------------------------------------------------------------------
  // F2/AC-1 (EP-158): a hand-rolled snapshot of the loaded invoice's visible
  // fields, taken ONCE (this component has no RHF, so no isDirty to lean
  // on the way LeaseFields does). Compared in handleSubmit below so a
  // Back/Next revisit through the wizard that changed nothing sends no
  // PATCH — invoices_controller#update enqueues a tenant notice on every
  // PATCH of an unpaid invoice, so "no second notice" has to live entirely
  // client-side. _uid is local-only row identity, excluded from the
  // comparison (never sent to the API either).
  // -------------------------------------------------------------------------
  const snapshotItemsKey = (list: InvoiceItem[]) =>
    JSON.stringify(list.map(({ _uid, ...rest }) => rest))
  const [initialInvoiceSnapshot] = useState(() =>
    isEditing
      ? { billedAt, dueAt, invoicePeriod, itemsKey: snapshotItemsKey(items) }
      : null,
  )

  // -------------------------------------------------------------------------
  // Submit — routes through `persistInvoice()` so Save & Continue and the
  // Preview button share one save path. If Preview already created the
  // invoice in this session, this UPDATEs instead of POSTing again — the
  // user sees exactly one persisted row even if they previewed + advanced.
  // -------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // F2/AC-1: gated on formId (wizard embedding only — a standalone
    // /invoices/:id/edit page's explicit Save click should still PATCH even
    // with no changes, mirroring LeaseFields' formId-gated isDirty skip).
    if (
      isEditing && !!formId && initialInvoiceSnapshot &&
      billedAt === initialInvoiceSnapshot.billedAt &&
      dueAt === initialInvoiceSnapshot.dueAt &&
      invoicePeriod === initialInvoiceSnapshot.invoicePeriod &&
      snapshotItemsKey(items) === initialInvoiceSnapshot.itemsKey
    ) {
      onSave(invoice)
      return
    }
    if (!isExternalParker && !leaseId) return
    // Use normalizedTotal: discounts are negated before submit so net-zero/
    // negative totals (allowed by backend) are not silently blocked here.
    if (!billedAt || !dueAt) {
      addToast({ type: 'error', message: t('errors.fields.billed_at') })
      return
    }
    if (normalizedTotal === 0) {
      addToast({ type: 'error', message: t('form.error_zero_total') })
      return
    }

    try {
      const saved = await persistInvoice()
      markSubmitted()
      onSave(saved)
    } catch (err: unknown) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Items table content — shared between streamlined and standalone layouts.
  // Extracted to avoid JSX duplication; references state/handlers defined above.
  const itemsTableContent = (
    <>
      {/* F16/AC-10 (EP-158): on a phone the Thai title ("รายการในใบแจ้งหนี้")
          wrapped onto two lines beside the add-item button in a single
          justify-between row. Below sm it stacks (title on its own line,
          button on the next); sm+ returns to the original side-by-side row. */}
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h2 className="text-lg font-semibold">{t('items.title')}</h2>
        {/* T149-022 (LSE-7): a paid invoice's restricted edit has no
            add/remove controls at all, items and the total are locked. */}
        {!restrictedMode && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={addItem}
          >
            {t('actions.add_item')}
          </Button>
        )}
      </div>

      {(priceCheck.isPending || isRepricing) && (
        <div className="flex items-center gap-2 text-sm text-base-content/60 mb-4">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          {t('form.loading_pricing')}
        </div>
      )}

      {/* Desktop header row (hidden on small screens) */}
      <div className="hidden sm:grid sm:grid-cols-12 gap-3 text-sm font-medium text-base-content/60 px-1 mb-2">
        <div className="sm:col-span-3">{t('items.type')}</div>
        <div className="sm:col-span-3">{t('items.description')}</div>
        <div className="sm:col-span-2">{t('items.quantity')}</div>
        <div className="sm:col-span-2">{t('items.unit_price')}</div>
        <div className="sm:col-span-1">{t('items.amount')}</div>
        <div className="sm:col-span-1" />
      </div>

      <div className="space-y-4">
        {items.map((item, index) => {
          const isMeteredZero =
            ['water', 'electricity', 'internet'].includes(item.item_type) &&
            item.quantity === 0
          return (
          <div key={item._uid ?? index}>
            {/* Desktop row — align cells to the TOP so a taller cell (e.g. the
                common-fee "3% of subtotal" note under the unit price) doesn't
                vertically off-center the rest of the row. */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 items-start [&>*]:min-w-0">
              <div className="sm:col-span-3">
                <Select
                  value={item.item_type}
                  onChange={(e) => updateItem(index, 'item_type', e.target.value)}
                  options={optionsForItem(item.item_type)}
                  disabled={restrictedMode}
                />
              </div>
              <div className="sm:col-span-3">
                <Input
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  placeholder={t('items.description')}
                  readOnly={restrictedMode}
                  aria-readonly={restrictedMode ? 'true' : undefined}
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  type="number"
                  value={Number.isFinite(item.quantity) ? item.quantity : ''}
                  onChange={(e) => updateItem(index, 'quantity', toFiniteNumber(e.target.value))}
                  min={0}
                  readOnly={restrictedMode || item.item_type === 'discount' || (isEngineOwned(item.item_type) && !isMeteredType(item.item_type))}
                  aria-readonly={restrictedMode || item.item_type === 'discount' || (isEngineOwned(item.item_type) && !isMeteredType(item.item_type)) ? 'true' : undefined}
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  type="number"
                  value={Number.isFinite(item.unit_price) ? item.unit_price : ''}
                  onChange={(e) =>
                    updateItem(index, 'unit_price', toFiniteNumber(e.target.value))
                  }
                  min={item.item_type === 'discount' ? undefined : 0}
                  step="0.01"
                  readOnly={restrictedMode || isEngineOwned(item.item_type)}
                  aria-readonly={restrictedMode || isEngineOwned(item.item_type) ? 'true' : undefined}
                  className={item.item_type === 'discount' ? 'text-warning' : undefined}
                />
                {item.item_type === 'common_fee' && commonFeeRate != null && (
                  <span className="text-xs text-base-content/50">
                    {t('items.percentage_basis', { rate: Math.round(commonFeeRate) })}
                  </span>
                )}
              </div>
              <div className="sm:col-span-1 flex items-center gap-0.5">
                {item.item_type === 'discount' && (
                  <Minus className="w-3 h-3 text-warning shrink-0" aria-hidden="true" />
                )}
                <span className={`font-medium text-sm${item.item_type === 'discount' ? ' text-warning' : ''}`}>
                  {formatBaht(
                    item.item_type === 'discount' ? Math.abs(item.amount) : item.amount,
                    { decimals: 2 },
                  )}
                </span>
              </div>
              <div className="sm:col-span-1 flex items-center">
                {!restrictedMode && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-error"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    aria-label={tc('actions.delete')}
                  />
                )}
              </div>
            </div>

            {/* Mobile card stack */}
            <div className="sm:hidden border border-base-200 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-base-content/60">
                  {/* F16/AC-10 (EP-158): this card means "item N", not
                      "type N" — the old key read "ประเภท 1" on a Thai phone,
                      where "type" names the per-row SELECT, not the card. */}
                  {t('items.item_label', { n: index + 1 })}
                </span>
                {!restrictedMode && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-error -mr-2"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    aria-label={tc('actions.delete')}
                  />
                )}
              </div>
              <Select
                label={t('items.type')}
                value={item.item_type}
                onChange={(e) => updateItem(index, 'item_type', e.target.value)}
                options={optionsForItem(item.item_type)}
                disabled={restrictedMode}
              />
              <Input
                label={t('items.description')}
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                placeholder={t('items.description')}
                readOnly={restrictedMode}
                aria-readonly={restrictedMode ? 'true' : undefined}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={t('items.quantity')}
                  type="number"
                  value={Number.isFinite(item.quantity) ? item.quantity : ''}
                  onChange={(e) => updateItem(index, 'quantity', toFiniteNumber(e.target.value))}
                  min={0}
                  readOnly={restrictedMode || item.item_type === 'discount' || (isEngineOwned(item.item_type) && !isMeteredType(item.item_type))}
                  aria-readonly={restrictedMode || item.item_type === 'discount' || (isEngineOwned(item.item_type) && !isMeteredType(item.item_type)) ? 'true' : undefined}
                />
                <Input
                  label={t('items.unit_price')}
                  type="number"
                  value={Number.isFinite(item.unit_price) ? item.unit_price : ''}
                  onChange={(e) =>
                    updateItem(index, 'unit_price', toFiniteNumber(e.target.value))
                  }
                  min={item.item_type === 'discount' ? undefined : 0}
                  step="0.01"
                  readOnly={restrictedMode || isEngineOwned(item.item_type)}
                  aria-readonly={restrictedMode || isEngineOwned(item.item_type) ? 'true' : undefined}
                  className={item.item_type === 'discount' ? 'text-warning' : undefined}
                  hint={
                    item.item_type === 'common_fee' && commonFeeRate != null
                      ? t('items.percentage_basis', { rate: Math.round(commonFeeRate) })
                      : undefined
                  }
                />
              </div>
              <div className="flex justify-end text-sm font-medium items-center gap-0.5">
                <span className="text-base-content/60 mr-1">{t('items.amount')}:</span>
                {item.item_type === 'discount' && (
                  <Minus className="w-3 h-3 text-warning shrink-0" aria-hidden="true" />
                )}
                <span className={item.item_type === 'discount' ? 'text-warning' : undefined}>
                  {formatBaht(
                    item.item_type === 'discount' ? Math.abs(item.amount) : item.amount,
                    { decimals: 2 },
                  )}
                </span>
              </div>
            </div>
            {/* BUG-5: non-blocking hint for metered lines with qty=0 */}
            {isMeteredZero && (
              <p className="mt-1 text-xs text-warning" role="note">
                {t('form.metered_zero_qty_hint')}
              </p>
            )}
          </div>
          )
        })}

        {/* Total row + (wizard-only) Preview PDF action */}
        <div className="border-t border-base-200 pt-4 flex justify-between items-center gap-3 flex-wrap">
          {formId ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<FileText className="w-4 h-4" aria-hidden="true" />}
              onClick={handlePreviewPdf}
              loading={isPreviewingPdf}
              disabled={normalizedTotal === 0 || items.length === 0}
              data-testid="invoice-preview-pdf"
            >
              {t('actions.preview_pdf')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-baseline gap-3">
            <span className="text-base-content/60">{tc('labels.total')}:</span>
            <span className="text-2xl font-bold leading-none">
              {formatBaht(normalizedTotal, { decimals: 2 })}
            </span>
          </div>
        </div>
      </div>
    </>
  )

  return (
    // EP-73 Tier-1 telemetry: first edit anywhere in the form marks started.
    <form id={formId} onSubmit={handleSubmit} onChangeCapture={markStarted} className={className}>
      {/* T149-022 (LSE-7 decision A): only the owner reaches this form on a
          paid invoice, and only billed_at/due_at/billing_period are
          editable; items and the total below render read-only. */}
      {restrictedMode && (
        <Alert variant="info" className="mb-6">
          {t('form.restricted_edit_notice')}
        </Alert>
      )}
      {/* EP-86 T013: Essentials / Advanced section grouping.
          streamlinedMode=false (standalone /invoices/new, /invoices/generate):
            3-column grid with both Cards always visible.
          streamlinedMode=true (wizard step 7):
            vertical stack; Invoice Details (Essentials) visible, Items wrapped
            in an OptionalSection disclosure collapsed by default. */}
      <div className={streamlinedMode ? 'space-y-6' : 'grid gap-6 lg:grid-cols-3'}>
        {/* Invoice Details — Essentials (always visible). In the wizard the
            fields sit in a compact 2-column grid so the line items get the
            focus; standalone keeps the single-column stack. */}
        <Card>
          <h2 className="text-lg font-semibold mb-4">{t('form.invoice_details')}</h2>
          <div className={streamlinedMode ? 'grid gap-4 md:grid-cols-2' : 'space-y-4'}>
            {isExternalParker ? (
              /* External parker: locked customer display */
              <div>
                <div className="text-sm font-medium mb-1">{t('labels.customer')}</div>
                <div className="input input-bordered flex items-center bg-base-200 text-base-content/70 cursor-not-allowed">
                  {externalCustomer?.name ?? `Customer #${externalCustomerId}`}
                </div>
              </div>
            ) : (
              <>
                {showPropertyFilter && (
                  <Select
                    label={tc('labels.property')}
                    value={propertyFilterId ? String(propertyFilterId) : ''}
                    onChange={(e) => {
                      setPropertyFilterId(e.target.value ? Number(e.target.value) : null)
                      setLeaseId('')
                    }}
                    // Locked in the wizard — the property was chosen in step 1.
                    disabled={streamlinedMode}
                    options={[
                      {
                        value: '',
                        label: tc('labels.all'),
                      },
                      ...properties.map((p) => ({ value: String(p.id), label: p.name })),
                    ]}
                  />
                )}

                <SearchableDropdown
                  label={t('labels.lease')}
                  name="lease_id"
                  value={leaseId}
                  onChange={(val) => setLeaseId(val)}
                  options={leaseOptions}
                  placeholder={t('form.select_lease')}
                  // Locked in the wizard — the lease was created in step 5.
                  disabled={isEditing || streamlinedMode}
                  onSearch={handleLeaseSearch}
                  isLoading={isLoadingLeases}
                  onLoadMore={() => setLeasePage((p) => p + 1)}
                  hasMore={hasMoreLeases}
                />

                {/* Customer auto-fill row */}
                {selectedLease && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-1">
                      {t('labels.customer')}
                      {showCustomerBadge && <PreFilledBadge />}
                    </div>
                    <div className="input input-bordered flex items-center bg-base-200 text-base-content/70 cursor-not-allowed">
                      {preFilledCustomerName ?? '—'}
                    </div>
                  </div>
                )}

                {/* Unit auto-fill row */}
                {selectedLease && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-1">
                      {t('labels.unit')}
                      {showUnitBadge && <PreFilledBadge />}
                    </div>
                    <div className="input input-bordered flex items-center bg-base-200 text-base-content/70 cursor-not-allowed">
                      {preFilledUnitName ?? '—'}
                    </div>
                  </div>
                )}
              </>
            )}

            <DateInput
              label={t('labels.billed_date')}
              value={billedAt}
              onChange={(e) => setBilledAt(e.target.value)}
              required
              dateFormat={format}
              timezone={timezone}
              calendar={calendar}
              locale={i18n.language}
            />
            <div>
              <DateInput
                label={t('labels.due_date')}
                value={dueAt}
                onChange={(e) => {
                  dueAtEditedRef.current = true
                  setDueAt(e.target.value)
                }}
                required
                dateFormat={format}
                timezone={timezone}
                calendar={calendar}
                locale={i18n.language}
              />
              {/* MON-8 E / T149-100: non-blocking. billed_at is the real
                  issue date (MON-10) so a due date before it is legitimate,
                  just worth flagging. Plain string compare: both are
                  YYYY-MM-DD date-only values. */}
              {dueAt && billedAt && dueAt < billedAt && (
                <p className="mt-1 text-xs text-warning" role="note">
                  {t('form.due_before_issue_warning')}
                </p>
              )}
            </div>
            <Select
              label={t('labels.billing_period')}
              value={invoicePeriod}
              onChange={(e) => setInvoicePeriod(e.target.value)}
              options={billingPeriodOptions}
            />
          </div>
        </Card>

        {/* Invoice Items + Preview panel — always visible (the line items are
            the point of this step; no collapsible). */}
        <Card
          className={streamlinedMode ? undefined : 'lg:col-span-2'}
          data-tour-target="invoice-preview"
        >
          {itemsTableContent}
        </Card>
      </div>

      {/* Standalone-only submit. In wizard mode the bottom-bar
          "Save & Continue" is the canonical create action; the inline
          Preview PDF above is the only review affordance left. */}
      {!formId && (
        <FormActions className="mt-6">
          {/* T149-022 (LSE-7): the restricted-edit design pairs the save
              action with an explicit Cancel back to the (unchanged) detail
              page. Every other invoice form has only the submit button. */}
          {restrictedMode && invoice && (
            <Button as={Link} to={`/invoices/${invoice.id}`} variant="ghost">
              {tc('actions.cancel')}
            </Button>
          )}
          <Button
            type="submit"
            loading={isEditing ? updateInvoice.isPending : createInvoice.isPending}
            disabled={normalizedTotal === 0}
          >
            {isEditing ? t('actions.update_invoice') : t('actions.create_invoice')}
          </Button>
        </FormActions>
      )}
    </form>
  )
}
